import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import {
	ContactDto,
	ContactDetailDto,
	RoleDto,
	EntityDto,
	EntityType,
	PuestoDto,
	EmployeeDto,
} from "../interfaces/contact.interface";
import { environment } from "src/environments/environment";

/**
 * Employee Info Response
 */
export interface EmployeeInfoResponse {
	nombre?: string;
	apellidos?: string;
	puesto?: string;
	email?: string;
	[key: string]: unknown;
}

@Injectable({
	providedIn: "root",
})
export class ContactService {
	private readonly apiUrl = `${environment.api.root}/Contacto`;

	constructor(private readonly http: HttpClient) {}

	/**
	 * Obtiene todos los contactos
	 */
	getContactsList(): Observable<ContactDetailDto[]> {
		return this.http.get<ContactDetailDto[]>(`${this.apiUrl}`);
	}

	/**
	 * Obtiene contactos filtrados por tipo y código de entidad
	 */
	getContactsByEntity(
		entityType: EntityType,
		entityCode: string,
	): Observable<ContactDetailDto[]> {
		const params = new HttpParams()
			.set("entityType", entityType)
			.set("entityCode", entityCode);

		return this.http.get<ContactDetailDto[]>(`${this.apiUrl}/GetByEntity`, {
			params,
		});
	}

	/**
	 * Obtiene contactos asociados a una entidad por ID y tipo
	 */
	getContactsByEntityId(
		entityId: number,
		entityType: string,
	): Observable<ContactDetailDto[]> {
		// Ensure we're using the correct API endpoint format
		return this.http.get<ContactDetailDto[]>(
			`${environment.api.root}/Contacto/by-entity/${entityType}/${entityId}`,
		);
	}

	/**
	 * Asocia un contacto existente a una entidad
	 */
	associateContactToEntity(
		contactId: number,
		entityId: number,
		entityType: string,
	): Observable<void> {
		const body = {
			contactId,
			entityId,
			entityType,
		};

		return this.http.post<void>(`${this.apiUrl}/AssociateToEntity`, body);
	}

	/**
	 * Desasocia un contacto de una entidad
	 */
	removeContactFromEntity(
		contactId: number,
		entityId: number,
		entityType: string,
	): Observable<void> {
		const params = new HttpParams()
			.set("contactId", contactId.toString())
			.set("entityId", entityId.toString())
			.set("entityType", entityType);

		return this.http.delete<void>(`${this.apiUrl}/RemoveFromEntity`, {
			params,
		});
	}

	/**
	 * Obtiene un contacto por ID
	 */
	getContactById(id: number): Observable<ContactDetailDto> {
		return this.http.get<ContactDetailDto>(`${this.apiUrl}/${id}`);
	}

	/**
	 * Crea un nuevo contacto
	 */
	createContact(contact: ContactDto): Observable<ContactDetailDto> {
		return this.http.post<ContactDetailDto>(this.apiUrl, contact);
	}

	/**
	 * Actualiza un contacto existente
	 */
	updateContact(
		id: number,
		contact: ContactDto,
	): Observable<ContactDetailDto> {
		return this.http.put<ContactDetailDto>(
			`${this.apiUrl}/Update/${id}`,
			contact,
		);
	}

	/**
	 * Elimina un contacto
	 */
	deleteContact(id: number): Observable<void> {
		return this.http.delete<void>(`${this.apiUrl}/${id}`);
	}

	/**
	 * Obtiene todos los roles disponibles (desde DAMA)
	 */
	getRoles(): Observable<RoleDto[]> {
		return this.http.get<RoleDto[]>(`${this.apiUrl}/GetRoles`);
	}

	/**
	 * Obtiene entidades disponibles por tipo
	 */
	getEntitiesByType(entityType: EntityType): Observable<EntityDto[]> {
		const params = new HttpParams().set("entityType", entityType);
		return this.http.get<EntityDto[]>(`${this.apiUrl}/GetEntitiesByType`, {
			params,
		});
	}

	/**
	 * Obtiene todos los puestos disponibles
	 */
	getPuestos(): Observable<PuestoDto[]> {
		return this.http.get<PuestoDto[]>(`${environment.api.root}/Puesto`);
	}

	/**
	 * Roles disponibles para una función agregada: generales + particulares de esa función,
	 * solo activos (ADR-0005). Con función vacía devuelve solo los generales (empleado sin función).
	 */
	getPuestosByFuncion(funcionAgregada: string): Observable<PuestoDto[]> {
		const params = new HttpParams().set(
			"funcionAgregada",
			funcionAgregada ?? "",
		);
		return this.http.get<PuestoDto[]>(
			`${environment.api.root}/Puesto/disponibles`,
			{ params },
		);
	}

	// ---- Admin de roles de persona (pantalla /admin/positions, ADR-0005) ----

	/** Funciones agregadas (maestro Workday, solo lectura, más las usadas en roles). */
	getFunciones(): Observable<string[]> {
		return this.http.get<string[]>(`${environment.api.root}/Puesto/funciones`);
	}

	createPuesto(puesto: Partial<PuestoDto>): Observable<{ id: number }> {
		return this.http.post<{ id: number }>(`${environment.api.root}/Puesto`, puesto);
	}

	updatePuesto(puesto: PuestoDto): Observable<{ id: number }> {
		return this.http.put<{ id: number }>(
			`${environment.api.root}/Puesto/${puesto.id}`,
			puesto,
		);
	}

	deletePuesto(id: number): Observable<{ id: number }> {
		return this.http.delete<{ id: number }>(`${environment.api.root}/Puesto/${id}`);
	}

	/** Número de contactos que usan el rol (decide entre borrar y desactivar). */
	getPuestoEnUso(id: number): Observable<number> {
		return this.http.get<number>(`${environment.api.root}/Puesto/${id}/en-uso`);
	}

	/**
	 * Obtiene empleados filtrados por función agregada
	 */
	getEmpleadosByFuncionAgregada(
		funcionAgregada: string,
	): Observable<EmployeeDto[]> {
		return this.http.get<EmployeeDto[]>(
			`${environment.api.root}/Empleado/ListEmpleadosByFuncionAgregada/${funcionAgregada}`,
		);
	}

	/**
	 * Obtiene información del empleado desde DAMA por matrícula
	 */
	getEmployeeInfo(matricula: string): Observable<EmployeeInfoResponse> {
		const params = new HttpParams().set("matricula", matricula);
		return this.http.get<EmployeeInfoResponse>(
			`${this.apiUrl}/GetEmployeeInfo`,
			{ params },
		);
	}
}
